import { NAV_GROUPS, PATHS, type NavItem, type Permission } from '@ctcells/core'
import { CURRENT_ACCOUNT } from '@ctcells/fixtures'
import { t, type MessageKey } from '@ctcells/i18n'
import { useCallback, useRef, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router'

import '../atoms/atoms.css' // .a-sr(시각적 숨김) — 이 파일은 원자를 쓰지 않아 직접 건다

/*
 * ⚠ `BrandLogo`(ROBOS 워드마크)는 **여기서 더 안 쓴다**(eric 지시 2026-09-23 「로보스 로고는 빼」).
 *   컴포넌트는 지우지 않았고 로그인 화면 · 태블릿 · 모듈 패드 · 장비 앱이 계속 쓴다 — `navIcons.tsx` 참조.
 */
import { CtcellsMark, NavIcon } from './navIcons.tsx'
import { useOutsideClick } from './useOutsideClick.ts'

import './shell.css'

/**
 * 콘솔 헤더 — 사이드바를 대신한다(eric 지시 2026-09-18 「사이드바 없어져」).
 *
 * ```
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ [CTCELLS]  [▦ 모니터링] [▤ 작업 관리] [⚗ 실험 준비] [🔔 알람 3] [⚙ 관리] [👤 이름] │
 * │ └ 클릭 = 모니터링(메뉴와 같은 곳)                                        │
 * └─────────────────────────────────────────────────────────────────────────┘
 * ```
 *
 * 🔴 **새로 만든 구조가 아니다.** 태블릿(`TabletLayout`)의 `padhead` + `tabnav` 와
 *    모듈 패드(`ModuleLayout`)의 계정 영역(`nav__acct`)을 합친 것이다 — 셋이 한 구조로 수렴한다.
 *
 * 🔴 **모니터링이 메뉴에 돌아왔다 — 「작업」 왼쪽**(eric 지시 2026-09-21 「헤더에 작업 왼쪽에
 *    모니터링 아이콘과 텍스트 넣어줘」). 종전엔 로고만 그 자리였다(2026-09-18). 로고 클릭도
 *    그대로 모니터링이다 — 길이 둘이지만 같은 곳으로 간다.
 * 🔴 **브랜드는 CTCells 워드마크 + 로고 모양의 「CONSOLE」 글자다**(eric 2026-09-28 「CTCELLS 로고
 *    스타일로 console 텍스트 붙여줄 수 있어?」). 2026-09-23 「헤더에 Console 문구 빼줘」로 글자를
 *    걷었던 것을 되돌렸다 — 단 옛 제품명 글자(`.padhead__tag`)가 아니라 **워드마크와 같은 색·캡 높이·
 *    굵기**의 글자다(`.padhead__product`). 접근 이름은 여전히 그림의 `aria-label` 이 진다. ROBOS 워드마크는 **헤더에서만** 빠졌다 — 컴포넌트는 남아 로그인 화면·
 *    태블릿·모듈 패드·장비 앱이 계속 쓴다(「원래 로고는 지우지 마」).
 * 🔴 **로고와 제품명은 «한 줄»이다**(eric 지시 2026-09-21 — 「이게 원래 좌측 메뉴 때문에 저렇게
 *    위치한 거야」). 세로 쌓임은 사이드바 시절의 흔적이라 `.padhead__brand--row` 로 갈랐다.
 *    ⚠ 태블릿·모듈 패드는 이 변형을 안 붙인다 — 그쪽은 세로 그대로다(`shell.css` 참조).
 * 🔴 **권한 없는 메뉴를 숨기지 않는다.** 자물쇠 + 비활성 + 사유다(§5-6). 숨김은 보안이 아니다.
 *    집행은 서버 403 `INSUFFICIENT_ROLE`.
 * 🔴 **접기/펴기 토글과 그룹 라벨은 없다.** 사이드바가 없으니 접을 것도, 묶을 것도 없다.
 * ⚠ **테마 전환은 여기 없다.** 콘솔은 「내 계정 › 개인 설정」이 쥔다(eric 지시 2026-09-17).
 *   설정 화면이 없는 표면(패드 · 태블릿)에만 `ThemeToggle` 이 남는다.
 */
export interface ConsoleHeaderProps {
  /**
   * 서버가 준 보유 권한. `undefined` = 아직 모름 → 잠금 표시를 하지 않는다.
   * 🔴 프론트가 역할에서 권한을 유도하지 않는다. 여기 값은 노출(자물쇠)에만 쓴다.
   */
  permissions?: readonly Permission[]
  /**
   * 메뉴 배지. **활성 알람 건수는 「알람」 메뉴에 붙는다**(2026-09-22 eric 지시로 알람이
   * 「작업」의 탭에서 제 문으로 나왔다 — 붙는 자리의 출처는 `useAlarmBadge.ts`).
   *
   * 🔴 **수는 셸이 채운다**(`useAlarmBadge.ts`). 여기 넘긴 값이 그것을 이긴다(시험·스토리 용도).
   * ⚠ 숫자 하나만 넘기면 중립 면이다. 위험(`ERROR`·`CRITICAL`)을 말하려면 `NavBadge` 를 준다.
   */
  badges?: Partial<Record<string, number | NavBadge>>
  /**
   * 계정 메뉴에 찍히는 값.
   * ⚠ `detail` 은 와이어프레임이 이메일을 넣었으나 **계약에 이메일이 없다** —
   *   실물에서는 표시 이름이 온다.
   */
  account?: { name: string; detail?: string; role?: string }
  /**
   * ⚠ **자동 로그아웃 잔여 시간 자리는 걷었다**(eric 지시 2026-09-21 「저거 시간 그냥 빼자」).
   *   서버가 무활동 잔여(UAM-SES-06)를 내려주지 않아 실물에서는 늘 `—` 였다 → BR-53.
   *   값이 오면 되살린다. **프론트가 카운트다운을 돌려 만들지 마라**(DP-04).
   */
  appVersion?: string
  onLogout?: () => void
}

/**
 * 메뉴 배지 한 개.
 *
 * 🔴 **색이 혼자 말하지 않는다**(§5-8). `tone` 은 «얹는» 축이고, 글리프(종 ↔ 경고 삼각형)와
 *    숫자와 `label`(스크린리더·툴팁)이 같은 사실을 따로 말한다. 색을 못 보는 사람도,
 *    흑백 토큰으로 서는 표면(장비 앱 · 모듈 패드)도 같은 뜻을 읽는다.
 */
export interface NavBadge {
  /** 서버가 준 건수 그대로. 🔴 `0` 은 배지를 만들지 않는다(만드는 쪽의 규율) */
  count: number
  /** `danger` = 심각(`ERROR`·`CRITICAL`)이 섞여 있다. 색은 강조 1 + 위험 1 뿐이다 */
  tone: 'neutral' | 'danger'
  /** 소리로 읽히는 문구. 🔴 숫자만으로는 무엇의 수인지 알 수 없다 */
  label: string
}

/**
 * 🔸 인증 API 가 붙기 전 기본 표시값. 서버가 준 값이 오면 그걸로 덮인다.
 * 역할은 원값(`admin`)이 아니라 표기로 바꿔서 넣는다 — 화면에 원값을 흘리지 않는다.
 */
const DEMO_ACCOUNT = {
  name: CURRENT_ACCOUNT.name,
  detail: CURRENT_ACCOUNT.email,
  role: t(`role.${CURRENT_ACCOUNT.role}` as MessageKey),
}

/**
 * 헤더에 싣는 메뉴 — `NAV_GROUPS` 를 펼친 그대로다(모니터링 · 작업 관리 · **실험 준비 · 알람** · 관리).
 * 이름은 사전(`nav.item.*`) · 아이콘은 `navIcons.tsx`(디자인팀 시안 2026-10-01 의 16 격자 선 아이콘).
 * 🔴 목록의 출처는 `NAV_GROUPS` 하나다. 여기서 항목을 새로 적지 않는다 —
 *    적으면 사이드바 시절처럼 두 벌이 되고 반드시 어긋난다.
 */
const HEADER_NAV: readonly NavItem[] = NAV_GROUPS.flatMap((group) => group.items)

export function ConsoleHeader({
  permissions,
  badges,
  account = DEMO_ACCOUNT,
  appVersion,
  onLogout,
}: ConsoleHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const closeMenu = useCallback(() => setMenuOpen(false), [])
  useOutsideClick(menuRef, menuOpen, closeMenu)

  const detail = account.detail
  const locked = (item: NavItem): boolean =>
    permissions !== undefined && !permissions.includes(item.permission)

  return (
    <header className="padhead">
      {/* 로고도 모니터링 진입이다 — 메뉴의 「모니터링」과 같은 곳 */}
      <NavLink
        className="padhead__brand padhead__brand--row"
        title={t('nav.brandHome')}
        to={PATHS.monitoring}
      >
        {/*
         * CTCells 워드마크 + 「CONSOLE」 글자(eric 2026-09-28 「CTCELLS 로고 스타일로 console 텍스트」).
         * 2026-09-23 에 글자를 걷었다가(「헤더에 Console 문구 빼줘」) 로고 모양으로 되돌렸다.
         *
         * 🔴 **접근 이름은 그림의 `aria-label` 하나가 진다**(`CONSOLE_PRODUCT` = 「CTCells Console」).
         *    글자는 `aria-hidden` 이다 — 둘 다 읽히면 「CTCells Console Console」이 된다. 이름은
         *    `CtcellsMark` 안에서 상수 한 곳에서 온다(`packages/core/src/modules.ts`).
         *    ⚠ 글자에서 `aria-hidden` 을 떼려면 그림 라벨을 「CTCells」로 줄여야 한다 — 둘은 한 묶음이다.
         * 🔴 **PNG 가 아니라 인라인 SVG 다**(eric 2026-09-23 「그럼 저거 SVG 로 바꿔서 수정해 주면
         *    되잖아」). 비트맵은 잉크가 구워져 있어 다크 테마에서 가라앉았다 — 지금은 `currentColor`
         *    라 `.padhead__mark` 의 `color` 한 줄이 테마를 태운다.
         * 🔴 **ROBOS 워드마크(`BrandLogo`)는 헤더에서 뺐지 «지우지» 않았다**(같은 날 「원래 로고는
         *    지우지 마」). 컴포넌트와 SVG 는 `navIcons.tsx` 에 그대로 있고 태블릿 · 모듈 패드 ·
         *    장비 앱이 계속 쓴다 — 여기서 안 부를 뿐이다.
         * 🔴 `viewBox` 가 글자 bounding box 라 아래 모서리가 곧 기준선이다 —
         *    `.padhead__brand--row` 의 `baseline` 정렬에 그대로 맞는다(`shell.css` 참조).
         * ⚠ `padhead__tag`(옛 제품명 글자) 는 태블릿·모듈 패드가 계속 쓴다 — 여기 글자와 다른 규칙이다.
         */}
        <CtcellsMark className="padhead__mark" />
        {/* 원문은 「Console」, 대문자는 CSS 가 입힌다(워드마크가 전부 대문자라서) — `shell.css` */}
        <span aria-hidden="true" className="padhead__product">
          Console
        </span>
      </NavLink>

      <nav aria-label={t('nav.aria')} className="tabnav">
        {HEADER_NAV.map((item) => {
          const isLocked = locked(item)
          const badge = normalizeBadge(badges?.[item.id])
          /*
           * 🔴 **문 안의 다른 경로에 있어도 그 문이 «현재»다**(eric 2026-09-22 「관리 › 모듈 탭이 선택되면 헤더의 관리
           *    선택이 없어진다」). `NavLink` 는 `to` 의 접두어만 보므로 `/modules`·`/logs`(관리) · `/recipes`·`/labware`
           *    (준비)에서는 불이 꺼졌다. 그 경로들은 `NavItem.also` 가 쥐고 여기서 같이 본다.
           */
          const here = (path: string) => pathname === path || pathname.startsWith(`${path}/`)
          const isActive = here(item.path) || (item.also ?? []).some(here)
          return (
            /* `NavLink` 대신 `Link` — NavLink 는 `aria-current` 를 제 판정으로 덮어써서 문 안의 다른 경로를 «현재»로 못 만든다 */
            <Link
              aria-current={isActive ? 'page' : undefined}
              aria-disabled={isLocked || undefined}
              className={isActive ? 'tabnav__item active' : 'tabnav__item'}
              key={item.id}
              onClick={(event) => {
                if (isLocked) event.preventDefault()
              }}
              title={isLocked ? t('reason.noPermission', { perm: item.permission }) : undefined}
              to={item.path}
            >
              <NavIcon id={item.id} />
              {t(item.labelKey)}
              {isLocked ? (
                <>
                  <svg aria-hidden="true" className="tabnav__lock" viewBox="0 0 24 24">
                    <rect fill="currentColor" height="10" rx="1.5" width="14" x="5" y="10" />
                    <path
                      d="M8 10V7a4 4 0 0 1 8 0v3"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                    />
                  </svg>
                  <span className="a-sr">
                    {t('reason.noPermission', { perm: item.permission })}
                  </span>
                </>
              ) : null}
              {/* 잠긴 항목에는 배지를 달지 않는다 — 못 여는 화면의 건수는 알릴 값이 아니다 */}
              {isLocked ? null : <NavBadgeMark badge={badge} />}
            </Link>
          )
        })}
      </nav>

      <div className="padhead__auth" ref={menuRef}>
        <div className="nav__acct">
          <button
            aria-expanded={menuOpen}
            aria-label={t('app.userMenu')}
            className={`nav__user${menuOpen ? ' is-open' : ''}`}
            onClick={() => setMenuOpen((open) => !open)}
            type="button"
          >
            {/* 🔴 글자 아바타(이름 첫 글자 배지)를 만들지 마라(§1-2). 사람 모양 도형이다 */}
            <svg
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21c0-4.2 3.6-7 8-7s8 2.8 8 7" />
            </svg>
            {/* ⚠ 이름 한 줄이다 — 잔여 시간 줄(`nav__sess`)은 걷었다(위 머리말 · BR-53) */}
            <span className="nav__uname">{account.name}</span>
          </button>

          {menuOpen ? (
            <div className="menu menu--right" role="menu">
              {detail !== undefined || account.role !== undefined ? (
                <div className="menu__head">
                  {detail}
                  {account.role ? <span className="menu__role">{account.role}</span> : null}
                </div>
              ) : null}
              <NavLink
                className="menu__item"
                onClick={closeMenu}
                role="menuitem"
                to={PATHS.account}
              >
                {t('nav.item.account')}
              </NavLink>
              {appVersion ? (
                <div className="menu__item menu__item--static">
                  {t('app.version')}
                  <span className="menu__kbd num">{appVersion}</span>
                </div>
              ) : null}
              <div className="menu__sep" />
              <button
                className="menu__item"
                onClick={() => {
                  closeMenu()
                  if (onLogout) onLogout()
                  else void navigate(PATHS.login)
                }}
                role="menuitem"
                type="button"
              >
                {t('app.logout')}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  )
}

/**
 * 숫자 하나만 넘긴 옛 호출부를 **중립 배지**로 올린다. `0` 은 배지가 아니다.
 * 🔴 여기서 문구를 지어내는 것은 그 하위호환 경로뿐이고, 실제 화면은 셸이 준 `label` 을 쓴다.
 */
function normalizeBadge(value: number | NavBadge | undefined): NavBadge | undefined {
  if (value === undefined) return undefined
  if (typeof value !== 'number') return value.count === 0 ? undefined : value
  return value === 0
    ? undefined
    : { count: value, tone: 'neutral', label: t('nav.alarmBadge', { count: value }) }
}

/**
 * 배지 한 개 — **숫자만**이다(eric 2026-09-22 「알람 아이콘이 또 들어가. 숫자만 들어가 있어야지」 —
 * 메뉴 자체가 이미 종 아이콘을 달고 있어 글리프가 겹쳤다).
 *
 * 심각 이상 여부는 **겉모습으로 갈리지 않는다** — 위험 배지의 테도 면과 같은 적색이라 붉은 알약 하나로
 * 보인다(eric 2026-09-28). 그 구분은 `label` 이 「… · 심각 이상 포함」으로 읽는다. 지우지 마라.
 */
function NavBadgeMark({ badge }: { badge: NavBadge | undefined }) {
  if (badge === undefined) return null
  return (
    <span
      aria-label={badge.label}
      className={`tabnav__badge tabnav__badge--${badge.tone}`}
      title={badge.label}
    >
      <span className="num">{badge.count}</span>
    </span>
  )
}
